import React from "react";
import {
    Box,
    FormControlLabel,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { SECTION_IDS, SECTION_META } from "./quoteSectionConfig";

const FIELD_DEFS = {
    shop_type: {
        label: "Shop type & environment",
        placeholder: "Fab footprint, cranes, bays, cleanliness…",
        minRows: 2,
    },
    machining_cam_consult: {
        label: "Machining & CAM",
        placeholder: "Setups, probing, cycle-time unknowns, consulting vs shop-run…",
        minRows: 3,
    },
    robot_implementation: {
        label: "Robot integration",
        placeholder: "Cells, EOAT, guarding, commissioning, runoff…",
        minRows: 3,
    },
    estimated_time: {
        label: "Time & labor bands",
        placeholder: "Engineering hours, install visits, FAT/SAT windows…",
        minRows: 2,
    },
    travel: {
        label: "Travel & expenses",
        placeholder: "Drive/flights, lodging, per-diem…",
        minRows: 2,
    },
    materials_needed: {
        label: "Materials you supply",
        placeholder: "Steel, cabling, hardware, lead times…",
        minRows: 2,
    },
    customer_material_actions: {
        label: "Customer-supplied actions",
        placeholder: "Who orders blanks, certs, onsite prep…",
        minRows: 2,
    },
    scheduling_planning: {
        label: "Scheduling & planning",
        placeholder: "Outage windows, training, deliverables…",
        minRows: 2,
    },
    software_requirements: {
        label: "Software stack",
        placeholder: "PLC/CAM versions, dongles, VPN, SaaS…",
        minRows: 2,
    },
    customer_equipment: {
        label: "Machines on-site",
        placeholder: "Existing robots, machine tools, facility limits…",
        minRows: 2,
    },
    other_notes: {
        label: "Other notes",
        placeholder: "Exclusions, risks, commercial terms…",
        minRows: 3,
    },
};

function SectionCard({ sectionId, form, setForm }) {
    const meta = SECTION_META[sectionId];
    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5 }}>
                {meta.label}
            </Typography>
            <Stack spacing={2}>
                {meta.fields.map((fieldKey) => {
                    const def = FIELD_DEFS[fieldKey];
                    return (
                        <TextField
                            key={fieldKey}
                            label={def.label}
                            placeholder={def.placeholder}
                            fullWidth
                            multiline
                            minRows={def.minRows}
                            value={form[fieldKey] || ""}
                            onChange={(e) => setForm((f) => ({ ...f, [fieldKey]: e.target.value }))}
                        />
                    );
                })}
            </Stack>
        </Paper>
    );
}

export default function QuoteScopeSections({ form, setForm, sectionFlags, setSectionFlags }) {
    const enabledCount = SECTION_IDS.filter((id) => sectionFlags[id]).length;

    const toggle = (id) => (e) => {
        setSectionFlags((prev) => ({ ...prev, [id]: e.target.checked }));
    };

    return (
        <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: "action.hover" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
                    Which scope blocks apply?
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    Turn sections on for this job only — hidden blocks stay saved if you turn them back on.{" "}
                    {enabledCount} of {SECTION_IDS.length} visible.
                </Typography>
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        columnGap: 1,
                    }}
                >
                    {SECTION_IDS.map((id) => (
                        <FormControlLabel
                            key={id}
                            control={
                                <Switch
                                    size="small"
                                    checked={Boolean(sectionFlags[id])}
                                    onChange={toggle(id)}
                                />
                            }
                            label={
                                <Typography variant="body2" sx={{ fontWeight: sectionFlags[id] ? 600 : 400 }}>
                                    {SECTION_META[id].shortLabel}
                                </Typography>
                            }
                            sx={{ mr: 1, ml: 0 }}
                        />
                    ))}
                </Box>
            </Paper>

            {enabledCount === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                    No scope sections enabled — flip a switch above to start capturing details.
                </Typography>
            ) : (
                <Stack spacing={2}>
                    {SECTION_IDS.filter((id) => sectionFlags[id]).map((id) => (
                        <SectionCard key={id} sectionId={id} form={form} setForm={setForm} />
                    ))}
                </Stack>
            )}
        </Stack>
    );
}
